import type { UserSettings, UserProfile } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { useDefaultMutation } from '~/lib/react-query';
import { hideLoader } from '~/common';
import FetchPromise from '~/agent/fetch-promise';
import { USERS_API_ROOT } from '../constants';

interface UpdateUserProfileParams extends Pick<UserProfile, 'avatar' | 'title' | 'pronouns' | 'bio' | 'id'> {
  preferred_name: string;
}
interface UpdateUserSettingsParams extends Pick<UserSettings, 'avatar' | 'name' | 'id' | 'language'> {
  email_settings: UserSettings['emailSettings'];
}

export type UserParams = Partial<UpdateUserProfileParams | UpdateUserSettingsParams>;

export function updateUser({ id, ...params }: UserParams): Promise<UserSettings | UserProfile> {
  return FetchPromise.put(`${USERS_API_ROOT}/${id}`, params);
}

export interface UpdateUserAvatarParams {
  id: UserParams['id'];
  avatar: FormData;
}

export function updateUserAvatar({ id, avatar }: UpdateUserAvatarParams): Promise<UserSettings | UserProfile> {
  // Existing api helpers have embedded assumptions about content type and process data
  // so doing an ad hoc request until we can revisit
  return new Promise<any>((resolve, reject) => {
    $.ajax({
      url: `/api/${USERS_API_ROOT}/${id}`,
      type: 'PUT',
      data: avatar,
      processData: false,
      contentType: false,
      success: (result) => {
        resolve(result);
      },
      error: (xhr) => {
        hideLoader();
        reject(xhr);
      },
    });
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  return useDefaultMutation(updateUser, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['settings']);
      await queryClient.invalidateQueries(['profile']);
    },
  });
}

export function useUpdateUserAvatarMutation() {
  const queryClient = useQueryClient();
  return useDefaultMutation(updateUserAvatar, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['settings']);
      await queryClient.invalidateQueries(['profile']);
    },
  });
}
